import React, { useState } from 'react';
import { CallToActionButton } from '../callToActionButton/CallToActionButton';
import { toastFail, toastInfo } from '../../utilities/toast';
import download from 'downloadjs';
import useApiService from '../../utilities/useApiService';
import { useParams } from 'react-router-dom';
import { Button, Fade, Menu, MenuItem, Tooltip } from '@material-ui/core';

interface ExcelDownloadMenuProps {
   buttonText: string;
   buttonWidth?: string;
   buttonHeight: string;
   availableReports: string[];
   hideReports?: string[];
   documentType?: 'excel' | 'pdf';
}

export const GenerateReportSelector: React.FC<ExcelDownloadMenuProps> = ({
   buttonText,
   buttonWidth,
   buttonHeight,
   availableReports,
   hideReports,
   documentType,
}) => {
   const { getMethod } = useApiService();
   const params: any = useParams();

   const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

   const options = [
      { label: 'Initial Report', type: 'INITIAL-REPORT', docType: 'pdf' },
      { label: 'Schedule Initial Report', type: 'SCHEDULE-INITIAL', docType: 'pdf' },
      { label: 'Return Report', type: 'RETURN-REPORT', docType: 'excel' },
      { label: 'Initial Schedule II', type: 'SCHEDULE-2', docType: 'pdf' },
      { label: 'Initial Schedule III-V', type: 'SCHEDULE-345', docType: 'pdf' },
      { label: 'Schedule II', type: 'SCHEDULE-2', docType: 'excel' },
      { label: 'Schedule III-V', type: 'SCHEDULE-345', docType: 'excel' },
   ];

   const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
   };

   const handleClose = () => setAnchorEl(null);

   const handleDownload = async (
      reportType: string,
      documentType: 'excel' | 'pdf' = 'excel'
   ) => {
      handleClose();
      toastInfo(`Generating ${reportType}`);
      try {
         const response = await getMethod(
            `/pharmacies/${params.pharmacyId}/returnrequests/${params.requestId}/${documentType}?reportType=${reportType}`,
            { responseType: 'blob' }
         );
         if (response && response.status >= 400) {
            toastFail(`Failed to generate ${reportType} report!`);
         } else {
            const contentType = response.headers['content-type'] as string;
            const contentDisposition = response.headers[
               'content-disposition'
            ] as string;

            const filename = contentDisposition
               ?.split('filename=')[1]
               ?.replace(/["']/g, '');

            download(response.data, filename, contentType);
         }
      } catch (error) {
         toastFail(`Error generating ${reportType}!`);
         console.error(error);
      }
   };

   return (
      <>
         <CallToActionButton
            text={buttonText}
            width={buttonWidth}
            height={buttonHeight}
            onClick={handleClick}
         />
         <Menu
            anchorEl={anchorEl}
            open={!!anchorEl}
            onClose={handleClose}
            keepMounted
            anchorOrigin={{
               vertical: 'bottom',
               horizontal: 'right',
            }}
            transformOrigin={{
               vertical: 'top',
               horizontal: 'right',
            }}
         >
            {options
               .filter((option) => !hideReports?.includes(option.type) && option.docType === documentType)
               .map((option) => {
                  const isDisabled = !availableReports.includes(option.type);
                  return (
                     <Tooltip
                        key={option.type}
                        title={
                           isDisabled
                              ? 'This report is not available for this request'
                              : ''
                        }
                        placement="left"
                     >
                        <div>
                           <MenuItem
                              disabled={isDisabled}
                              onClick={() =>
                                 handleDownload(option.type, documentType)
                              }
                           >
                              {option.label}
                           </MenuItem>
                        </div>
                     </Tooltip>
                  );
               })}
         </Menu>
      </>
   );
};
